import type React from 'react'
import type { JSX, MouseEvent } from 'react'
import type { IImagePoster } from 'atoms/ImagePoster'
import type { BANNER_BACK_BUTTON_INDENT } from 'vars/index'
import type { BannerCursor } from './Banner.h'

import { useEffect, useMemo, useRef, useState } from 'react'
import { FallbackImageSizes } from 'atoms/FallbackImage'
import { Icon, IconNames, IconSizes } from 'atoms/Icon'
import { ImagePoster } from 'atoms/ImagePoster'
import {
  PartnerProgramCoverDesktop,
  PartnerProgramCoverMobile,
} from 'atoms/PartnerProgramCover'
import { CardCorners } from 'blocks/Cards'
import { ContentStateBadge } from 'blocks/ContentStateBadge'
import { BannerSkeleton } from 'blocks/Skeletons'
import {
  useClientWidth,
  useIntersectionObserver,
  useOrientation,
} from 'utils/hooks'
import { BANNER_OVERLAY_WIDTH } from 'vars/banner-overlay-width'
import {
  BANNER_ASPECT_RATIO,
  BANNER_PADDINGS,
  BANNER_WIDTH,
  COLOR,
} from 'vars/index'
import {
  BannerAlignItems,
  BannerJustifyContent,
  BannerOverlay,
  BannerOverlayDirection,
} from './Banner.h'
import {
  BannerContent,
  BannerSC,
  LockedImage,
  OverlayLayout,
  PosterContainer,
} from './Banner.styled'
import { useBannerBackroundCover } from './lib'

export interface IBannerProps {
  children?: React.ReactNode
  width?: BANNER_WIDTH
  aspectRatio?: BANNER_ASPECT_RATIO
  justifyContent?: BannerJustifyContent
  alignItems?: BannerAlignItems
  overlay?: BannerOverlay | undefined
  overlayHorizontal?: BannerOverlay | undefined
  overlayVertical?: BannerOverlay | undefined
  overlayDirection?: BannerOverlayDirection | undefined
  overlayEnabled?: boolean
  overlayWidth?: number | undefined
  overlayHeight?: number | undefined
  posterUrl?: string
  posterMobileUrl?: string
  paddings?: BANNER_PADDINGS
  backButtonIndent?: BANNER_BACK_BUTTON_INDENT
  fallbackImageSize?: IImagePoster['fallbackImageSize']
  isCoverFallbackImage?: boolean
  cursor?: BannerCursor
  className?: string
  style?: React.CSSProperties
  onClick?: (event: MouseEvent) => void
  corners?: CardCorners
  isPartialPoster?: boolean
  noContentInside?: boolean
  singleBanner?: any
  locked?: boolean
  lockedIconSize?: IconSizes
  lockedIconColor?: COLOR
  horizontalCovers?: Record<string, any>
  verticalCovers?: Record<string, any>
  title?: string
  /** Called when banner becomes visible in viewport */
  onBecameVisible?: () => void
  visibilityThreshold?: number
  isTrailerPlayerVisible?: boolean
  isClone?: boolean
  shouldRenderTrailerPlayer?: boolean
  showAclBlockedLabel?: boolean
  loading?: boolean
}

/** Matches `MEDIA.MAX_1239`: trailer overlay is vertical through 1239px (FP-4289). */
const COMPACT_TRAILER_MAX_WIDTH = 1239

export const Banner = ({
  children,
  width = BANNER_WIDTH.SIZE_100,
  aspectRatio = BANNER_ASPECT_RATIO.SIZE_10,
  justifyContent = BannerJustifyContent.Center,
  alignItems = BannerAlignItems.Start,
  overlay = BannerOverlay.SURFACE_OPACITY_DARK_40,
  overlayHorizontal = BannerOverlay.BANNER_PARTIAL_HORIZONTAL_OVERLAY,
  overlayVertical = BannerOverlay.BANNER_VERTICAL_OVERLAY,
  overlayDirection,
  overlayEnabled = true,
  overlayHeight,
  overlayWidth,
  posterUrl = '',
  posterMobileUrl = '',
  paddings = BANNER_PADDINGS.SIZE_10,
  backButtonIndent,
  fallbackImageSize,
  cursor,
  onClick,
  className = '', // some props for Linaria
  style,
  corners = CardCorners.CORNERS_0,
  isCoverFallbackImage,
  isPartialPoster = true,
  noContentInside,
  singleBanner,
  locked,
  lockedIconSize = IconSizes.xxBigger,
  lockedIconColor = COLOR.DISABLED,
  horizontalCovers,
  verticalCovers,
  title,
  onBecameVisible,
  visibilityThreshold = 0.5,
  isTrailerPlayerVisible = false,
  isClone = false,
  shouldRenderTrailerPlayer = false,
  showAclBlockedLabel = false,
  loading = false,
}: IBannerProps): JSX.Element => {
  const bannerRef = useRef<HTMLDivElement>(null)
  const clientWidth = useClientWidth()
  const { orientation } = useOrientation()
  const [posterError, setPosterError] = useState(false)
  const [bannerShown, setBannerShown] = useState(false)

  // Track banner visibility
  const isVisible = useIntersectionObserver(bannerRef, {
    threshold: visibilityThreshold,
  })

  // Use ref to store callback to avoid re-triggering effect on callback change
  const wasVisibleRef = useRef(false)
  const onBecameVisibleRef = useRef(onBecameVisible)
  onBecameVisibleRef.current = onBecameVisible

  // Call onBecameVisible only when banner becomes visible (not on every render).
  // Skip for infinite-loop clones (see useBannerElements) to avoid firing twice.
  useEffect(() => {
    if (isVisible && !wasVisibleRef.current) {
      wasVisibleRef.current = true
      if (!isClone) onBecameVisibleRef.current?.()
      setBannerShown(true)
    } else if (!isVisible) {
      wasVisibleRef.current = false
    }
  }, [isVisible, isClone])

  useEffect(() => {
    if (!shouldRenderTrailerPlayer) {
      setBannerShown(false)
    }
  }, [shouldRenderTrailerPlayer])

  const isMobile = clientWidth > 0 && clientWidth < 600
  // Matches MEDIA.MAX_1239: the trailer is pinned 16:9 at the top through
  // 1239px, so the overlay must also fade in from below rather than from the
  // side used on 1240+ (FP-4289).
  const isCompactTrailerViewport =
    clientWidth > 0 && clientWidth <= COMPACT_TRAILER_MAX_WIDTH

  const isCompactTrailerVisible =
    isCompactTrailerViewport && isTrailerPlayerVisible && !isClone
  const effectiveOverlayDirection = isCompactTrailerVisible
    ? BannerOverlayDirection.OVERLAY_VERTICAL
    : overlayDirection
  const effectiveOverlayHeight = isCompactTrailerVisible ? 100 : overlayHeight

  const mobileLandscape =
    clientWidth >= 600 && clientWidth < 640 && orientation === 'landscape'

  const poster = isMobile || mobileLandscape ? posterMobileUrl : posterUrl

  const partialTemplate =
    isPartialPoster &&
    (width === BANNER_WIDTH.SIZE_100 ||
      width === BANNER_WIDTH.SIZE_94 ||
      singleBanner)

  const bannerOverlayWidth = singleBanner
    ? BANNER_OVERLAY_WIDTH.SIZE_60
    : overlayWidth

  const partialAspectRatio = partialTemplate ? BANNER_ASPECT_RATIO.POSTER : null
  // update poster error when poster changes
  useEffect(() => {
    setPosterError(false)
  }, [poster])

  const covers = clientWidth <= 1239 ? verticalCovers : horizontalCovers

  const BackgroundCover = useMemo(
    () => (locked ? useBannerBackroundCover(title ?? '', covers) : <></>),
    [locked, title, covers]
  )

  const trailerTemplate = useMemo(() => {
    if (
      !children?.[1] ||
      !bannerShown ||
      !shouldRenderTrailerPlayer ||
      isClone
    ) {
      return null
    }

    return (
      <PosterContainer
        aspectRatio={partialAspectRatio}
        noContentInside={noContentInside}
        isTrailer
      >
        {children?.[1]}
      </PosterContainer>
    )
  }, [
    children,
    partialAspectRatio,
    noContentInside,
    bannerShown,
    shouldRenderTrailerPlayer,
    isClone,
  ])

  const posterTemplate = useMemo(() => {
    if (locked) {
      return (
        <PosterContainer>
          <LockedImage aspectRatio={partialAspectRatio}>
            <Icon
              name={IconNames.tvPinLock}
              size={lockedIconSize}
              color={lockedIconColor}
            />
            {BackgroundCover ? BackgroundCover : null}
          </LockedImage>
        </PosterContainer>
      )
    }

    // Clones never render the trailer, so they must keep their poster —
    // hiding it would leave the banner blank on loop wrap-around (FP-4222).
    if (isTrailerPlayerVisible && !isClone) {
      return null
    }

    if (loading && !poster) {
      return clientWidth !== 0 ? (
        <BannerSkeleton aspectRatio={aspectRatio} bannerWidth={width} />
      ) : (
        <></>
      )
    }

    if (isCoverFallbackImage && (!poster || posterError)) {
      return (
        <PosterContainer aspectRatio={partialAspectRatio}>
          <PartnerProgramCoverDesktop />
          <PartnerProgramCoverMobile />
        </PosterContainer>
      )
    }

    if (clientWidth !== 0) {
      return (
        <PosterContainer
          aspectRatio={partialAspectRatio}
          noContentInside={noContentInside}
        >
          <ImagePoster
            posterUrl={poster}
            alt='BannerImage'
            fallbackImageSize={
              fallbackImageSize ??
              (width === BANNER_WIDTH.SIZE_66
                ? FallbackImageSizes.M
                : FallbackImageSizes.XXL)
            }
            corners={corners}
            lazy={false}
            handleError={() => setPosterError(true)}
          />
        </PosterContainer>
      )
    }

    return <></>
  }, [
    clientWidth,
    poster,
    fallbackImageSize,
    aspectRatio,
    width,
    corners,
    partialAspectRatio,
    locked,
    BackgroundCover,
    isCoverFallbackImage,
    setPosterError,
    posterError,
    isTrailerPlayerVisible,
    isClone,
    trailerTemplate,
    loading,
  ])

  return (
    <BannerSC
      ref={bannerRef}
      className={className}
      style={style}
      width={width}
      aspectRatio={aspectRatio}
      cursor={cursor}
      onClick={onClick}
      corners={corners}
      /* An attribute rather than a styled prop: Linaria lifts every dynamic
         interpolation into a positional inline custom property, so a prop here
         would shift the indices BannerSC's existing styles are read by. */
      data-locked={locked ? 'true' : undefined}
    >
      {posterTemplate}
      {trailerTemplate}
      <OverlayLayout
        overlay={overlay}
        overlayHorizontal={overlayHorizontal}
        overlayVertical={overlayVertical}
        overlayDirection={effectiveOverlayDirection}
        overlayEnabled={overlayEnabled}
        overlayWidth={bannerOverlayWidth}
        overlayHeight={effectiveOverlayHeight}
        aspectRatio={partialAspectRatio}
      />
      {showAclBlockedLabel && !locked ? (
        <ContentStateBadge kind='GEOBLOCK' />
      ) : null}
      <BannerContent
        justifyContent={justifyContent}
        alignItems={alignItems}
        paddings={paddings}
        backButtonIndent={backButtonIndent}
      >
        {(children as React.ReactNode[])?.length ? children![0] : children}
      </BannerContent>
    </BannerSC>
  )
}
